import { useEffect, useState, useCallback } from "react";
import { Timeline } from "primereact/timeline";
import { Button } from "primereact/button";

import { themeClasses as T, C } from "sj-ui-toolkit/src/atlas/utils";

import { useSession } from "../../contexts/SandboxHooks";
import { useLearnContext } from "../../../../../context/LearnContext";
import { useTournamentContext } from "../../../../../context/TournamentContext";
import { useStatusOverrides } from "../../helpers/useStatusOverrides";

/**
 * SandboxStatusLoader - Displays a centered loading card for sandbox status changes
 * Overlays the sandbox tabs and buttons with a semi-transparent background
 * Shows a timeline of the sandbox initialization stages
 */
const SandboxStatusLoader = () => {
  const { isTournament } = useLearnContext();
  const { beginTournamentLesson } = useTournamentContext();
  const { session, resetSandbox } = useSession();
  const statusOverrides = useStatusOverrides();

  const overallStatus = statusOverrides?.overall || session?.overall || "initial";
  const sandboxStatus = statusOverrides?.sandbox || session?.sandbox?.status || "Unknown";

  // Defer showing loader for 250ms to avoid flicker
  const [showDeferredLoader, setShowDeferredLoader] = useState(isTournament);
  useEffect(() => {
    if (overallStatus !== "ready") {
      const timer = setTimeout(() => {
        setShowDeferredLoader(true);
      }, 250);
      return () => clearTimeout(timer);
    } else {
      if (!isTournament) {
        setShowDeferredLoader(false);
      }
    }
  }, [overallStatus]);

  // Define the stages of sandbox initialization. The first and last ones will be hidden from the timeline
  const stages = [
    {
      label: "Requesting",
      status: "Unknown",
      description: "Requesting a new sandbox instance",
    },
    {
      label: "Provisioning",
      status: "Unavailable",
      description: "Starting up a new sandbox instance",
    },
    {
      label: "Allocating",
      status: "Pending",
      description: "Allocating the sandbox to your account",
    },
    {
      label: "Configuring",
      status: "Configuring",
      description: "Configuring the sandbox environment",
    },
    {
      label: "Running",
      status: "Running",
      description: "Starting the sandbox services",
    },
  ];

  const stageIcons = {
    completed: "pi pi-check",
    working: "pi pi-spinner pi-spin",
    pending: "pi pi-circle",
  };

  const stageIconClass = {
    completed: "bg-primary",
    working: "bg-primary",
    pending: "bg-gray-300",
  };

  // Find the index of the current stage
  let currentStageIndex =
    overallStatus === "acquiring" ? stages.findIndex(s => s.status === sandboxStatus) : -1;

  // Get friendly status message and icon
  const getStatusTitle = () => {
    switch (overallStatus) {
      case "initial":
        return "Requesting Sandbox";
      case "acquiring":
        return "Getting your sandbox ready";
      case "error":
        return "Sandbox Error";
      default:
        return "Loading";
    }
  };

  // Timeline events - show completed and current stages, filtering out the first and last stages
  const timelineEvents = stages
    .map((stage, index) => {
      const status =
        index < currentStageIndex
          ? "completed"
          : index === currentStageIndex
            ? "working"
            : "pending";

      return {
        label: stage.label,
        status,
        icon: stageIcons[status],
        iconClass: stageIconClass[status],
      };
    })
    .filter((_, index) => index > 0 && index < stages.length - 1);

  // Handle Begin button click for tournaments
  const handleBegin = useCallback(() => {
    beginTournamentLesson();
    setShowDeferredLoader(false);
  }, [beginTournamentLesson]);

  if (!showDeferredLoader) {
    return null;
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-50 rounded-l-sm -m-3">
      <div className={C("rounded-lg shadow-lg p-8 max-w-md", T.bgElevation2)}>
        {/* Only shown for tournaments */}
        {overallStatus === "ready" ? (
          <div className="text-center w-sm" role="status">
            <div className="flex justify-center mb-6">
              <i className={C("pi pi-check-circle text-5xl", T.textSuccess)} />
            </div>
            <h2 className={C("text-3xl font-bold mb-3")}>Sandbox Ready</h2>
            <p className={C("mb-6", T.textSecondary)}>
              Your sandbox is ready to use. When you are ready to start, click Begin below.
            </p>
            <Button label="Begin" icon="pi pi-arrow-right" onClick={handleBegin} />
          </div>
        ) : overallStatus === "error" ? (
          <div className="text-center w-sm" role="alert">
            <div className="flex justify-center mb-6">
              <i className={C("pi pi-times-circle text-5xl", T.textError)} />
            </div>
            <h2 className={C("text-3xl font-bold mb-3")}>Sandbox Error</h2>
            <p className={C("mb-6", T.textSecondary)}>
              {session?.error?.message || "Failed to initialize sandbox"}
            </p>
            <Button label="Retry" icon="pi pi-refresh" onClick={() => resetSandbox()} />
          </div>
        ) : (
          <div role="status">
            <h2 className={C("text-2xl font-bold mb-6 text-center")}>{getStatusTitle()}</h2>
            <div className="flex items-center gap-6">
              <div className="w-2/5 p-5 text-right">
                {stages[currentStageIndex]?.description || "Loading..."}
              </div>
              <Timeline
                value={timelineEvents}
                layout="vertical"
                align="left"
                pt={{
                  opposite: { className: "hidden" },
                  content: { className: "pt-1" },
                }}
                content={item => item.label}
                marker={item => (
                  <div
                    className={C(
                      "rounded-full w-8 h-8 flex items-center justify-center",
                      item.iconClass
                    )}
                  >
                    <i className={C("pi", item.icon)} />
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SandboxStatusLoader;
